// Foldertree.jsx
// Vertical Foldertree for quick folder selection.

// Copyright HS Analysis GmbH, 2023
// Authors: Matthias Wagner, Nina Sebold, Viktor Eberhardt

// Framework imports
import React from "react";
import { useHistory } from "react-router-dom";

// MUI imports
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import { Button } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TreeView from "@mui/lab/TreeView";

// HSA KIT Components
import { FileViewerContext } from "../FileViewer";
import Backend from "../../common/utils/Backend.jsx";
import FileFormatUtils from "../../common/utils/FileFormatUtils.js";
import RecursiveFolderEntity from "./FolderEntity.jsx";

const useStyles = makeStyles({
  root: {
    height: "100%",
    background: "#fff",
    // borderLeft: "5px solid  #EBEBEB",
    display: "grid",
    gridTemplateRows: "auto 1fr",
    overflow: "hidden",
  },
  treeContainer: {
    height: "100%",
    overflow: "auto",
  },
  button: {
    height: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
});

/**
 * Vertical Foldertree for quick folder selection.
 */
export default function Foldertree() {
  const classes = useStyles();
  const FV = React.useContext(FileViewerContext);
  const history = useHistory();
  const {
    topLevelEntities,
    setTopLevelEntities,
    expanded,
    updateExpanded,
    openFolder,
    setOpenFolder,
  } = FV;

  const [updateKey, setUpdateKey] = React.useState(performance.now());
  const treeContainerRef = React.useRef(null);

  const handleScroll = React.useCallback(() => {
    if (treeContainerRef.current) {
      localStorage.setItem(
        "folderTreeScrollPosition",
        treeContainerRef.current.scrollTop
      );
    }
  }, []);

  // Restore scroll position on mount
  React.useEffect(() => {
    if (treeContainerRef.current) {
      const savedScrollPosition = localStorage.getItem(
        "folderTreeScrollPosition"
      );
      if (savedScrollPosition) {
        treeContainerRef.current.scrollTop = parseInt(savedScrollPosition, 10);
      }
    }
  }, [topLevelEntities]); // Re-run when entities are loaded

  /*
  Loading Folder list and set Foldertree width
  Trigger: Initialise
  */
  React.useEffect(() => {
    let isMounted = true;
    Backend.walkDir("", true).then((entities) => {
      const filteredEntities = FileFormatUtils.filterViewerEntities(entities);
      if (isMounted) {
        const sortedEntities =
          FileFormatUtils.sortEntitiesByTypeAndName(filteredEntities);
        setTopLevelEntities(sortedEntities);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    const sortedEntities = FileFormatUtils.sortEntitiesByTypeAndName(
      topLevelEntities,
      FV.folderSortBy,
      FV.folderSortReverse,
      FV.filePreviewSortReverse
    );
    setTopLevelEntities(sortedEntities);
    setUpdateKey(performance.now());
  }, [FV.folderSortBy, FV.folderSortReverse]);

  /*
  Function for Button "Open Previous/Collapse All" 
  */
  const handleCollapseAll = () => {
    if (!(FV.caseId === null || FV.caseId === undefined)) {
      FV.setCaseId(null);
      FV.setSelCase(null);
      FV.setTreeWidth(526);
      // FV.setPathList([]);
      history.push(`/file_view/`);
      FV.resetRenderer();
      updateExpanded([]);
      setOpenFolder([]);
    } else {
      setOpenFolder(expanded);
      if (expanded.length > 0) {
        updateExpanded([]);
      } else {
        updateExpanded(openFolder);
      }
    }
  };

  //maybe add snackbar msg if no previous folder found

  return (
    <div className={classes.root}>
      <Button className={classes.button} onClick={handleCollapseAll}>
        {!(FV.caseId === null)
          ? "Close Case"
          : expanded.length > 0
          ? "Collapse All"
          : "Open Previous Folder"}
      </Button>
      <Box
        ref={treeContainerRef}
        onScroll={handleScroll}
        className={classes.treeContainer}
      >
        <TreeView
          selected={FV.activeFilePath}
          key={updateKey}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          expanded={expanded}
          onNodeToggle={(event, nodeIds) => updateExpanded(nodeIds)}
        >
          {topLevelEntities.map((tle) => (
            <RecursiveFolderEntity key={tle.path} entity={tle} />
          ))}
        </TreeView>
      </Box>
    </div>
  );
}
